import { Component, Input, OnChanges } from '@angular/core';

interface TicketPlace {
  place: string;
  link: string | null;
}
@Component({
  selector: 'order-detail-headband',
  templateUrl: './order-detail-headband.component.html',
  styleUrls: ['./order-detail-headband.component.css'],
})
export class OrderDetailHeadbandComponent implements OnChanges {
  @Input() public order: any | null = null;

  isLoading = false;
  @Input() set loading(value: boolean) {
    this.isLoading = value;
  }

  places: TicketPlace[] = [];
  dates: string[] = [];

  constructor() {}

  ngOnChanges() {
    const tickets = this.order?.tickets || [];

    tickets.forEach((ticket: any) => {
      const start_on = ticket.ticketType.start_on ?? this.order?.event.startAt;
      const _place = ticket.ticketType.place ?? this.order?.event.place;
      const placeLink = ticket.ticketType.link ?? this.order?.event.link;

      if (start_on && !this.dates.find((date) => date == start_on)) {
        this.dates.push(start_on);
      }

      this.dates.sort();

      if (_place && !this.places.find((place) => place.place == _place)) {
        this.places.push({ place: _place, link: placeLink });
      }
    });
  }

  openAllLocations() {
    if (!this.places || this.places.length === 0) {
      return;
    }

    this.places.forEach((place) => {
      let url: string;
      if (place.link) {
        url = place.link;
      } else {
        url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          place.place
        )}`;
      }
      window.open(url, '_blank');
    });
  }

  addToCalendar() {
    if (!this.order?.event || !this.dates || this.dates.length === 0) {
      return;
    }

    const eventName = this.order.event.name || 'Evento';
    const firstDate = this.dates[0];
    const location = this.places.map((p) => p.place).join(', ') || '';

    // Convertir fecha a formato ISO y calcular fecha de fin (asumiendo 2 horas de duración)
    const startDate = new Date(firstDate);
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 2);

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      this.downloadICSFile(eventName, startDate, endDate, location);
    } else {
      this.openGoogleCalendar(eventName, startDate, endDate, location);
    }
  }

  private openGoogleCalendar(
    eventName: string,
    startDate: Date,
    endDate: Date,
    location: string
  ) {
    // Formatear fechas para Google Calendar (YYYYMMDDTHHMMSS)
    const formatDateForGoogle = (date: Date): string => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };

    const startStr = formatDateForGoogle(startDate);
    const endStr = formatDateForGoogle(endDate);

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventName,
      dates: `${startStr}/${endStr}`,
      location: location,
      details: eventName,
    });

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(googleCalendarUrl, '_blank');
  }

  private downloadICSFile(
    eventName: string,
    startDate: Date,
    endDate: Date,
    location: string
  ) {
    // Formatear fechas en formato iCalendar (YYYYMMDDTHHMMSSZ)
    const formatDate = (date: Date): string => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    };

    const uid = `event-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}@tix`;

    // Crear contenido del archivo .ics
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//TIX//Event Calendar//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${formatDate(new Date())}`,
      `DTSTART:${formatDate(startDate)}`,
      `DTEND:${formatDate(endDate)}`,
      `SUMMARY:${this.escapeICS(eventName)}`,
      `LOCATION:${this.escapeICS(location)}`,
      `DESCRIPTION:${this.escapeICS(eventName)}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${eventName.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private escapeICS(text: string): string {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  }

  isGoogleMapsLink(url: string | null | undefined): boolean {
    if (!url) return false;
    const link = url.toLowerCase();
    return (
      link.includes('google.com/maps') ||
      link.includes('maps.google.com') ||
      link.includes('maps.app.goo.gl') ||
      link.includes('goo.gl/maps')
    );
  }
}
